import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cx } from '@/lib/cx';
import { Icon } from './Icon';
import { EmptyState, ErrorState, SkeletonRows } from './States';
import { InfoHint } from './InfoHint';
import { SelectBox, type Selection } from './Selection';
import { applyOrder, dropIndex, moveColumn, trackFor, type TableLayout } from './tableLayout';
import s from './DataTable.module.css';

export interface Column<T> {
  id: string;
  header: ReactNode;
  /** A CSS grid track: `120px`, `minmax(0,1fr)`. */
  width: string;
  cell: (row: T, index: number) => ReactNode;
  align?: 'left' | 'right' | 'center';
  mono?: boolean;
  /** Present → the column is sortable. Nulls sort last in either direction. */
  sortValue?: (row: T) => number | string | null | undefined;
  /** What the column measures, behind an ⓘ beside its header (the plans page's `InfoHint`). */
  info?: ReactNode;
}

export type SortState = { id: string; desc: boolean } | null;

export interface DataTableProps<T> {
  rows: T[] | undefined;
  columns: Column<T>[];
  getRowId: (row: T) => string;
  onRowClick?: (row: T, index: number) => void;
  selectedId?: string | null;
  /** "The app says this is live" — e.g. a 100% match row. */
  rowGlow?: (row: T) => boolean;
  rowMuted?: (row: T) => boolean;
  /**
   * Alternate rows take a faint fill and every row highlights under the pointer, clickable or not —
   * for dense numeric tables read across a row (Markets). Off by default.
   */
  striped?: boolean;
  rowHeight?: number;
  defaultSort?: SortState;
  /** Controlled sort, for server-sorted tables. */
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  empty?: ReactNode;
  /** Virtualise rows inside a scroll box of this height (long lists such as trades). */
  maxHeight?: number;
  /** Chevron at the row end, signalling the row opens something. */
  chevron?: boolean;
  ariaLabel: string;
  stickyHeader?: boolean;
  /** Rows can be picked for a bulk action (`useSelection`): a checkbox column is added first. */
  selection?: Selection;
  /** Keep the first column (and the checkbox column) in view while the table scrolls sideways. */
  pinFirstColumn?: boolean;
  /**
   * Widths and order the user has set (`tableLayout.ts`). With `onLayoutChange`, the header borders
   * drag to resize (double-click one to reset that column) and the headers drag to reorder; a
   * pinned first column stays first. Saved by the page, so the layout belongs to that table.
   */
  layout?: TableLayout;
  onLayoutChange?: (layout: TableLayout) => void;
}

/**
 * The narrowest a dragged column can be. Deliberately below what most content needs: a column
 * dragged narrow truncates with an ellipsis (text, chips) or narrows (the match bar) rather than
 * refusing to move.
 */
const MIN_COLUMN = 24;
/** A header press that travels less than this is a click (sort), not a drag. */
const DRAG_SLOP = 6;

/** Column gap and the checkbox column's width, in px — as `.head`/`.row` and `.checkCell` set them. */
const GAP = 16;
const CHECK = 20;

/** The narrowest a track can be: `120px` → 120, `minmax(160px, 1fr)` → 160, `1fr` → 0. */
export function trackMin(width: string): number {
  const w = width.trim();
  const px = /^(\d+(?:\.\d+)?)px$/.exec(w);
  if (px) return Number(px[1]);
  const mm = /^minmax\(\s*(\d+(?:\.\d+)?)px/.exec(w);
  return mm ? Number(mm[1]) : 0;
}

function compare(a: number | string | null | undefined, b: number | string | null | undefined) {
  const an = a == null || (typeof a === 'number' && Number.isNaN(a));
  const bn = b == null || (typeof b === 'number' && Number.isNaN(b));
  if (an && bn) return 0;
  if (an) return 1;
  if (bn) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

export function DataTable<T>({
  rows,
  columns,
  getRowId,
  onRowClick,
  selectedId,
  rowGlow,
  rowMuted,
  striped,
  rowHeight = 44,
  defaultSort = null,
  sort: controlledSort,
  onSortChange,
  loading,
  error,
  onRetry,
  empty,
  maxHeight,
  chevron,
  ariaLabel,
  stickyHeader,
  selection,
  pinFirstColumn,
  layout: savedLayout,
  onLayoutChange,
}: DataTableProps<T>) {
  const [localSort, setLocalSort] = useState<SortState>(defaultSort);
  const sort = controlledSort !== undefined ? controlledSort : localSort;
  const setSort = (next: SortState) => (onSortChange ? onSortChange(next) : setLocalSort(next));

  // The layout as dragged, ahead of the page saving it, so the table follows the pointer.
  const [draft, setDraft] = useState<TableLayout | null>(null);
  const layout = draft ?? savedLayout;
  const ordered = useMemo(() => applyOrder(columns, layout?.order), [columns, layout?.order]);
  const widths = ordered.map((c) => trackFor(c, layout));
  // The column being dragged, and the insertion point its header is over.
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);
  const headRow = useRef<HTMLDivElement>(null);
  // A drag ends with a click on the same header; that click must not also sort.
  const swallowClick = useRef(false);

  const template = (selection ? `${CHECK}px ` : '') + widths.join(' ') + (chevron ? ' 20px' : '');
  // **Wide tables scroll sideways rather than squeeze.** The grid never shrinks below what its
  // columns ask for (a watchlist with many strategies is a column per strategy), and the table
  // scrolls inside its panel instead of crushing every column into an ellipsis.
  const tracks = [...(selection ? [`${CHECK}px`] : []), ...widths, ...(chevron ? ['20px'] : [])];
  const minWidth = `calc(${tracks.reduce((sum, w) => sum + trackMin(w), 0) + GAP * (tracks.length - 1)}px + 2 * var(--panel-pad-x))`;
  const [scrolledX, setScrolledX] = useState(false);

  // **A second scrollbar above the header** while the table is wider than its panel, so a long
  // table can be scrolled sideways without first scrolling to its bottom. The two stay in step.
  const tableBox = useRef<HTMLDivElement>(null);
  const topBar = useRef<HTMLDivElement>(null);
  const [overflowWidth, setOverflowWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = tableBox.current;
    if (!el) return;
    const measure = () => setOverflowWidth(el.scrollWidth > el.clientWidth + 1 ? el.scrollWidth : null);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    if (el.firstElementChild) watch.observe(el.firstElementChild);
    return () => watch.disconnect();
  }, []);
  const onTableScroll = (left: number) => {
    if (topBar.current && topBar.current.scrollLeft !== left) topBar.current.scrollLeft = left;
    if (pinFirstColumn) setScrolledX(left > 0);
  };
  const onTopScroll = (left: number) => {
    if (tableBox.current && tableBox.current.scrollLeft !== left) tableBox.current.scrollLeft = left;
  };

  const sorted = useMemo(() => {
    if (!rows) return [];
    if (!sort || controlledSort !== undefined) return rows;
    const col = columns.find((c) => c.id === sort.id);
    if (!col?.sortValue) return rows;
    const get = col.sortValue;
    const out = [...rows].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      // Nulls stay last whichever way the sort runs.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const c = compare(va, vb);
      return sort.desc ? -c : c;
    });
    return out;
  }, [rows, sort, columns, controlledSort]);

  const scroller = useRef<HTMLDivElement>(null);
  const virtual = Boolean(maxHeight) && sorted.length > 60;
  const virtualizer = useVirtualizer({
    count: sorted.length,
    getScrollElement: () => scroller.current,
    estimateSize: () => rowHeight,
    overscan: 12,
    enabled: virtual,
  });

  const onHeaderClick = (col: Column<T>) => {
    if (swallowClick.current) {
      swallowClick.current = false;
      return;
    }
    if (!col.sortValue) return;
    if (sort?.id !== col.id) setSort({ id: col.id, desc: true });
    else if (sort.desc) setSort({ id: col.id, desc: false });
    else setSort(null);
  };

  const onRowKey = (e: KeyboardEvent<HTMLDivElement>, row: T, i: number) => {
    if (!onRowClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick(row, i);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const sib = (e.key === 'ArrowDown' ? e.currentTarget.nextElementSibling : e.currentTarget.previousElementSibling) as HTMLElement | null;
      sib?.focus();
    }
  };

  const firstMovable = pinFirstColumn ? 1 : 0;

  /** Drag a header's right border to set that column's width; the page is told on release. */
  const startResize = (col: Column<T>) => (e: ReactPointerEvent<HTMLElement>) => {
    if (!onLayoutChange || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const cell = e.currentTarget.parentElement;
    if (!cell) return;
    const startX = e.clientX;
    // The track, not the box: a ruled cell reaches into the gap (negative margin), so its box is
    // wider than its column by that much.
    const startWidth = cell.getBoundingClientRect().width + (parseFloat(getComputedStyle(cell).marginRight) || 0);
    const floor = MIN_COLUMN;
    const base = savedLayout ?? {};
    let next = { ...(base.widths ?? {}) };
    const move = (ev: PointerEvent) => {
      next = { ...next, [col.id]: Math.max(floor, Math.round(startWidth + ev.clientX - startX)) };
      setDraft({ ...base, widths: next });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      setDraft(null);
      onLayoutChange({ ...base, widths: next });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  /** Double-click a border to give that column its width back. */
  const resetWidth = (col: Column<T>) => {
    if (!onLayoutChange || !savedLayout?.widths?.[col.id]) return;
    const rest = { ...savedLayout.widths };
    delete rest[col.id];
    onLayoutChange({ ...savedLayout, widths: rest });
  };

  /** Drag a header sideways to move its column; a press that does not travel is the sort click. */
  const startDrag = (index: number) => (e: ReactPointerEvent<HTMLElement>) => {
    if (!onLayoutChange || e.button !== 0 || index < firstMovable) return;
    if ((e.target as HTMLElement).closest('[data-resizer], [data-no-drag]')) return;
    const head = headRow.current;
    if (!head) return;
    const startX = e.clientX;
    const id = ordered[index]!.id;
    let dragging = false;
    let target = index;
    const move = (ev: PointerEvent) => {
      if (!dragging) {
        if (Math.abs(ev.clientX - startX) < DRAG_SLOP) return;
        dragging = true;
        setDragId(id);
      }
      const rects = Array.from(head.querySelectorAll<HTMLElement>('[data-col]')).map((el) => el.getBoundingClientRect());
      target = Math.min(dropIndex(rects, ev.clientX, firstMovable), ordered.length);
      setDropAt(target);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (dragging) {
        swallowClick.current = true;
        const next = moveColumn(
          ordered.map((c) => c.id),
          index,
          target,
        );
        if (next.some((cid, k) => cid !== ordered[k]!.id)) onLayoutChange({ ...(savedLayout ?? {}), order: next });
      }
      setDragId(null);
      setDropAt(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const order = sorted.map(getRowId);
  const pickedOnScreen = selection ? order.filter((id) => selection.has(id)).length : 0;
  const pin = (k: number) => (pinFirstColumn && k <= (selection ? 1 : 0) ? s.pinned : undefined);
  const pinLeft = (k: number): CSSProperties | undefined =>
    pinFirstColumn && selection && k === 1 ? { left: CHECK + GAP } : undefined;

  const renderRow = (row: T, i: number, style?: CSSProperties) => {
    const id = getRowId(row);
    return (
      <div
        key={id}
        role="row"
        tabIndex={onRowClick ? 0 : undefined}
        aria-selected={selectedId != null ? selectedId === id : selection ? selection.has(id) : undefined}
        className={cx(
          s.row,
          striped && i % 2 === 1 && s.stripe,
          striped && s.hoverable,
          onRowClick && s.clickable,
          selectedId === id && s.selected,
          selection?.has(id) && s.picked,
          rowGlow?.(row) && s.glow,
          rowMuted?.(row) && s.muted,
        )}
        style={{ gridTemplateColumns: template, height: rowHeight, ...style }}
        onClick={onRowClick ? () => onRowClick(row, i) : undefined}
        onKeyDown={(e) => onRowKey(e, row, i)}
      >
        {selection && (
          <div role="cell" className={cx(s.checkCell, pin(0))}>
            <SelectBox className={s.check} checked={selection.has(id)} label="Select row" onToggle={(shift) => selection.toggle(id, shift, order)} />
          </div>
        )}
        {ordered.map((c, k) => (
          <div
            key={c.id}
            role="cell"
            className={cx(s.cell, c.mono && 'mono', c.align && s[c.align], k < ordered.length - 1 && s.ruled, pin(k + (selection ? 1 : 0)))}
            style={pinLeft(k + (selection ? 1 : 0))}
          >
            {c.cell(row, i)}
          </div>
        ))}
        {chevron && <Icon name="chevron_right" size={20} className={s.chevron} />}
      </div>
    );
  };

  let body: ReactNode;
  if (error) body = <ErrorState error={error} onRetry={onRetry} compact />;
  else if (loading || !rows) body = <SkeletonRows rows={6} height={rowHeight} />;
  else if (sorted.length === 0) body = empty ?? <EmptyState compact title="Nothing here yet" />;
  else if (virtual)
    body = (
      <div ref={scroller} className={s.scroller} style={{ maxHeight }}>
        <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
          {virtualizer.getVirtualItems().map((v) =>
            renderRow(sorted[v.index] as T, v.index, {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              transform: `translateY(${v.start}px)`,
            }),
          )}
        </div>
      </div>
    );
  else
    body = (
      <div className={maxHeight ? s.scroller : undefined} style={maxHeight ? { maxHeight } : undefined}>
        {sorted.map((r, i) => renderRow(r, i))}
      </div>
    );

  return (
    <div className={s.frame}>
    {overflowWidth != null && (
      <div ref={topBar} className={s.topScroll} onScroll={(e) => onTopScroll(e.currentTarget.scrollLeft)} aria-hidden>
        <div style={{ width: overflowWidth, height: 1 }} />
      </div>
    )}
    <div
      ref={tableBox}
      role="table"
      aria-label={ariaLabel}
      className={cx(s.table, selection && selection.count > 0 && s.picking, scrolledX && s.scrolledX)}
      onScroll={(e) => onTableScroll(e.currentTarget.scrollLeft)}
    >
      <div className={s.inner} style={{ minWidth }}>
      <div ref={headRow} role="row" className={cx(s.head, stickyHeader && s.sticky)} style={{ gridTemplateColumns: template }}>
        {selection && (
          <div role="columnheader" className={cx(s.checkCell, pin(0))}>
            <SelectBox
              checked={order.length > 0 && pickedOnScreen === order.length}
              indeterminate={pickedOnScreen > 0 && pickedOnScreen < order.length}
              label={pickedOnScreen === order.length && order.length > 0 ? 'Clear selection' : 'Select every row shown'}
              onToggle={() => selection.toggleAll(order)}
            />
          </div>
        )}
        {ordered.map((c, k) => {
          const active = sort?.id === c.id;
          const movable = Boolean(onLayoutChange) && k >= firstMovable;
          return (
            <div
              key={c.id}
              data-col={c.id}
              role="columnheader"
              aria-sort={active ? (sort.desc ? 'descending' : 'ascending') : undefined}
              className={cx(
                s.headCell,
                c.align && s[c.align],
                k < ordered.length - 1 && s.ruled,
                active && s.sorted,
                pin(k + (selection ? 1 : 0)),
                movable && s.movable,
                dragId === c.id && s.dragging,
                dropAt === k && s.dropBefore,
                dropAt === ordered.length && k === ordered.length - 1 && s.dropAfter,
              )}
              style={pinLeft(k + (selection ? 1 : 0))}
              title={typeof c.header === 'string' ? c.header : undefined}
              onPointerDown={onLayoutChange ? startDrag(k) : undefined}
            >
              {c.sortValue ? (
                // A real button: sorting from the keyboard, and the header reads as a control.
                <button type="button" className={s.sortButton} onClick={() => onHeaderClick(c)}>
                  <span className={s.headText}>{c.header}</span>
                  {active && <Icon name={sort.desc ? 'arrow_downward' : 'arrow_upward'} size={14} />}
                </button>
              ) : (
                <span className={s.headText}>{c.header}</span>
              )}
              {c.info && (
                <span className={s.headInfo} data-no-drag>
                  <InfoHint size={14} label={`About ${typeof c.header === 'string' ? c.header : 'this column'}`}>
                    {c.info}
                  </InfoHint>
                </span>
              )}
              {onLayoutChange && (
                <span
                  data-resizer
                  className={s.resizer}
                  title="Drag to resize · double-click to reset"
                  onPointerDown={startResize(c)}
                  onDoubleClick={() => resetWidth(c)}
                />
              )}
            </div>
          );
        })}
        {chevron && <span />}
      </div>
      {body}
      </div>
    </div>
    </div>
  );
}
